import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ContainerImageInfo,
  ContainerImageSearchResult,
} from '@back4app2/sdk';

import back4app2 from '../../back4app2';
import Button from '../Button';
import Skeleton from '../Skeleton';
import TextInput from '../FormInputField/TextInput/TextInput';
import { DOCKER_LOGO } from '../BuildMethodSelector/stackLogos';
import { ReactComponent as VisibilityIcon } from '../../assets/images/visibility-icon.svg';
import { ReactComponent as VisibilityOffIcon } from '../../assets/images/visibility-off-icon.svg';

// One provider ships today, and it is named in the field hints rather than
// asked for again. The field stays in the value so the SDK call and a second
// provider both have somewhere to land.
export type RegistryProvider = 'DOCKER_HUB';

export interface ContainerImageValue {
  provider: RegistryProvider;
  repository: string;
  tag: string;
  // Credentials, when the user has them. There is deliberately no "is this
  // private?" flag: that asks the user to classify the image before they can
  // supply a token, and getting the classification wrong produced a NOT_FOUND
  // with no obvious way out. What they know is whether they have credentials.
  username: string;
  password: string;
}

export const INITIAL_CONTAINER_IMAGE: ContainerImageValue = {
  provider: 'DOCKER_HUB',
  repository: '',
  tag: 'latest',
  username: '',
  password: '',
};

interface ContainerImageSourceProps {
  value: ContainerImageValue;
  onChange: (value: ContainerImageValue) => void;
  // Raised whenever the resolution settles, so the step can gate Next on a
  // real answer instead of on the fields merely being filled in.
  onResolved: (info: ContainerImageInfo | null) => void;
  onNext: () => void;
  // The panel header's search box, shared with the repository tab.
  searchQuery: string;
}

const SEARCH_DEBOUNCE_MS = 600;

// One and two letters match tens of thousands of repositories and rank by
// nothing useful, so the request is spent on an answer nobody can act on.
// Waiting for the third character removes most of the traffic a search box
// generates without the user ever noticing a delay.
const MIN_SEARCH_LENGTH = 3;

// The platform our nodes run. Stated in the worker and in the API resolver
// too; the dashboard repeats it rather than importing across the boundary.
const REQUIRED_PLATFORM = 'linux/amd64';

// The repository list's own row. Reused verbatim so a Docker Hub result and a
// GitHub repository read as the same kind of thing to pick.
// One typographic scale for this form. `normal-case` matters: TextInput
// capitalizes labels, which turns "Access token" into "Access Token" —
// against the design system's sentence-case rule. And without a size the
// label inherits the body size, which made field labels outrank the section
// heading above them.
// One label style on this screen, and the same one the wizard's other forms
// use. It had two — `text-sm` on the fields and a bolder 15px on the
// credentials heading — which read as two levels of importance where there is
// only one: they are all just labels.
const LABEL_CLASSNAME = 'text-[0.8125rem] font-medium normal-case text-text-dim';
const HINT_CLASSNAME = 'text-xs leading-140 text-text-faint';

const ROW_CLASSNAME =
  'flex justify-between items-center py-4 px-[1.875rem] bg-navy-800 border-b border-b-navy-500';

// One field carrying `username:token`, split at the FIRST colon only: the
// username cannot contain one, but a token can, and splitting on every colon
// would silently truncate it.
export function parseCredentials(text: string): {
  username: string;
  password: string;
} {
  const separator = (text || '').indexOf(':');

  if (separator < 0) {
    return { username: '', password: '' };
  }

  return {
    username: text.slice(0, separator).trim(),
    password: text.slice(separator + 1),
  };
}

function formatSize(sizeBytes?: number): string | null {
  if (!sizeBytes) {
    return null;
  }

  const mb = sizeBytes / (1024 * 1024);

  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
}

function formatCount(count: number): string {
  // Billions matter now. This used to format star counts, which top out in
  // the tens of thousands; pull counts for an official image run to eleven
  // figures, and without this rung `nginx` reported "13311.2M pulls" — a
  // number nobody can read at a glance, which is the only thing this column
  // is for.
  if (count >= 1000000000) {
    return `${(count / 1000000000).toFixed(1)}B`;
  }

  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }

  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`;
  }

  return `${count}`;
}

// Every one of these is a wrong answer the user can fix right now, in the
// field they are already looking at. Left to the deploy they arrive minutes
// later as a container that never started, blamed on something else.
function messageForError(
  errorCode: string | undefined,
  reference: string
): string {
  switch (errorCode) {
    case 'NOT_FOUND':
      // Also the answer for a private image asked for anonymously: Docker Hub
      // will not say which, so the message names the way out of both.
      return `We couldn't find ${reference} on Docker Hub, or it's private — add credentials below.`;
    case 'UNAUTHORIZED':
      return 'Docker Hub rejected these credentials.';
    case 'INCOMPLETE_CREDENTIALS':
      return 'Add both the username and the access token.';
    case 'RATE_LIMITED':
      return 'Docker Hub rate-limited us. Try again in a few minutes.';
    case 'UNSUPPORTED_PLATFORM':
      return "This image has no linux/amd64 variant — our nodes can't run it.";
    case 'INVALID_REFERENCE':
      return 'Enter an image like "nginx" or "acme/api".';
    case 'BLOCKED_IMAGE':
      // Says nothing about how we recognised it. The worker refuses this same
      // image at deploy whatever it is called, and naming the digest here
      // would just tell someone probing us which property to change.
      return "This image isn't allowed on Back4app Containers. Contact support if you think that's wrong.";
    default:
      return "We couldn't reach Docker Hub. Try again in a moment.";
  }
}

const ContainerImageSource = ({
  value,
  onChange,
  onResolved,
  onNext,
  searchQuery,
}: ContainerImageSourceProps) => {
  const { Logo: DockerLogo, color: dockerColor } = DOCKER_LOGO;

  // Browsing until an image is chosen, either off the list or typed by hand.
  // A private image never appears in a public search, so the manual path is
  // not a fallback for when search fails — it is the only route for a whole
  // class of image, and it stays one click away.
  const [picked, setPicked] = useState<ContainerImageSearchResult | null>(null);
  const [isManual, setIsManual] = useState(false);
  const [manualText, setManualText] = useState('');

  const [results, setResults] = useState<ContainerImageSearchResult[] | null>(
    null
  );
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(0);

  // The last answer and "a request is running" are separate state on purpose:
  // typing another character keeps the previous verdict on screen instead of
  // blanking the line on every keystroke.
  // The raw `username:token` text. Kept here rather than in the shared value
  // because a half-typed credential has no username and no token yet, and
  // deriving the field's contents from the parsed pair would erase what the
  // user is in the middle of writing.
  const [credentialsText, setCredentialsText] = useState(() =>
    value.username && value.password
      ? `${value.username}:${value.password}`
      : ''
  );
  const [showCredentials, setShowCredentials] = useState(false);

  const [info, setInfo] = useState<ContainerImageInfo | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const { provider, repository, tag, username, password } = value;
  const isConfiguring = Boolean(picked) || isManual;
  // Sent only as a complete pair — see the guard in the resolve effect.
  const hasCredentials = Boolean(username && password);

  // With a single field, "user:" parses to an empty token and would resolve
  // anonymously — a private image would then come back NOT_FOUND with nothing
  // on screen explaining why. Validate the parsed pair, not just the colon.
  const credentialsError = (() => {
    if (!credentialsText) {
      return undefined;
    }

    const parsed = parseCredentials(credentialsText);

    return parsed.username && parsed.password
      ? undefined
      : 'Use the format username:token.';
  })();

  const update = useCallback(
    (patch: Partial<ContainerImageValue>) => {
      // A verdict belongs to the values that produced it, so editing any of
      // them drops it. Leaving a green "Found" under a repository the user
      // has since changed is the worst of both worlds.
      setInfo(null);
      onResolved(null);
      onChange({ ...value, ...patch });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, value]
  );

  // Splits what the user typed into the two fields the configure view edits.
  // Deliberately forgiving: `nginx`, `acme/api` and `acme/api:1.4.2` all land
  // somewhere sensible, and anything malformed is caught by the resolve that
  // runs on the next screen — with a message about the image, which is better
  // than a validation error on a box in a list footer.
  const applyTypedImage = useCallback(() => {
    const typed = manualText.trim();

    if (!typed) {
      return;
    }

    // Only the LAST colon: a digest reference carries one of its own, and a
    // registry host may carry a port.
    const separator = typed.lastIndexOf(':');
    // A colon before the last slash belongs to a host, not to the tag.
    const hasTag = separator > typed.lastIndexOf('/');

    update({
      repository: hasTag ? typed.slice(0, separator) : typed,
      tag: hasTag ? typed.slice(separator + 1) : 'latest',
    });
    setIsManual(true);
  }, [manualText, update]);

  useEffect(() => {
    if (isConfiguring) {
      return;
    }

    if (!searchQuery || searchQuery.trim().length < MIN_SEARCH_LENGTH) {
      setResults(null);
      setIsSearching(false);

      return;
    }

    let cancelled = false;

    const timeout = setTimeout(async () => {
      const requestId = searchRef.current + 1;
      searchRef.current = requestId;

      setIsSearching(true);

      let found: ContainerImageSearchResult[] = [];

      try {
        found = await back4app2.containerImageSearch(provider, searchQuery);
      } catch (e) {
        found = [];
      }

      if (cancelled || searchRef.current !== requestId) {
        return;
      }

      setResults(found);
      setIsSearching(false);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchQuery, provider, isConfiguring]);

  // Resolved on Next, not while typing. A debounced check per keystroke sends
  // a burst of requests to Docker Hub for answers about half-typed names that
  // nobody asked for — and each one costs a token request plus a manifest
  // read. The answer only matters at the moment of moving on, so that is when
  // it is asked for.
  const checkAndContinue = async () => {
    setIsChecking(true);
    setInfo(null);
    onResolved(null);

    let result: ContainerImageInfo | null = null;

    try {
      result = await back4app2.containerImageInfo(
        provider,
        repository,
        tag,
        hasCredentials ? username : undefined,
        hasCredentials ? password : undefined
      );
    } catch (e) {
      result = null;
    }

    setIsChecking(false);
    setInfo(result);

    if (result && result.exists && !result.errorCode) {
      onResolved(result);
      onNext();
    }
  };

  const backToSearch = () => {
    setPicked(null);
    setIsManual(false);
    setInfo(null);
    onResolved(null);
    onChange({ ...INITIAL_CONTAINER_IMAGE, provider });
  };

  if (!isConfiguring) {
    let list;

    if (isSearching && !results) {
      list = [0, 1, 2].map((key) => (
        <div className={ROW_CLASSNAME} key={key}>
          <Skeleton className="h-4 w-80" />
          <Skeleton className="h-4 w-20" />
        </div>
      ));
    } else if (!searchQuery || searchQuery.trim().length < MIN_SEARCH_LENGTH) {
      list = (
        <div className="px-[1.875rem] py-16 text-center">
          <div className="font-sora font-semibold text-lg leading-140">
            Find an image to deploy
          </div>
          <div className="text-sm leading-140 mt-2 opacity-70">
            Search Docker Hub above, or enter an image by name.
          </div>
        </div>
      );
    } else if (results && results.length === 0) {
      list = (
        <div className="px-[1.875rem] py-16 text-center">
          <div className="font-sora font-semibold text-lg leading-140">
            Nothing matched "{searchQuery}"
          </div>
          <div className="text-sm leading-140 mt-2 opacity-70">
            Private images never appear in search — enter it by name below.
          </div>
        </div>
      );
    } else {
      list = (results || []).map((result) => {
        // Select is the only thing that selects — same rule as the repository
        // list. A whole-row target on a list this long turns a mis-scroll into
        // a committed choice, and the row already carries three things to read
        // before you decide (official, description, pulls) with nowhere safe
        // to rest the pointer.
        return (
          <div
            className="flex items-center gap-3.5 py-[1.125rem] px-[1.875rem] border-b border-b-card-line"
            key={result.repository}
          >
            <span className="flex-none w-[30px] h-[30px] rounded-surface bg-navy-700 flex items-center justify-center">
              <DockerLogo
                width="17"
                height="17"
                fill="currentColor"
                color={dockerColor}
              />
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center gap-2">
                <span className="text-[0.9375rem] font-medium leading-140 text-light-blue truncate">
                  {result.repository}
                </span>
                {result.isOfficial && (
                  <span className="flex-none text-[10px] leading-140 tracking-[0.08em] uppercase px-1.5 py-0.5 rounded bg-old-blue/[0.14] text-light-blue">
                    Official
                  </span>
                )}
              </span>
              {result.description && (
                <span className="block text-xs leading-140 mt-1 text-text-faint truncate">
                  {result.description}
                </span>
              )}
            </span>
            {/* Pulls, not stars. Stars are a popularity contest among people
                browsing Docker Hub; pulls are how many times the image was
                actually deployed, which is the closer thing to "is this the
                one everybody uses". `pullCount` was already on the result and
                nothing read it. */}
            <span className="flex-none text-xs leading-140 text-text-faint whitespace-nowrap [@media(max-width:639.98px)]:hidden">
              {formatCount(result.pullCount)} pulls
            </span>
            {/* The mockup ends this row with a chevron. A chevron is a hint
                that the row leads somewhere, which is true only if the row is
                the target — it is not, so the button says what it does. */}
            <Button
              type="primary"
              onClick={() => {
                setPicked(result);
                update({ repository: result.repository, tag: 'latest' });
              }}
            >
              Select
            </Button>
          </div>
        );
      });
    }

    return (
      <>
        {list}
        {/* Typing the reference straight in, instead of a link that swaps the
            panel for an empty form. The search only sees what Docker Hub's
            index returns — a private image is not in it, and neither is one
            whose name you already know — so this is the path for everything
            the list above cannot show, and it should not read as a fallback
            for people who failed at searching. */}
        <div className="px-[1.875rem] py-5 bg-field-blue/60 flex items-center gap-3 [@media(max-width:639.98px)]:flex-wrap">
          <span className="flex-none text-[0.8125rem] leading-140 text-text-dim">
            Private image, or not listed?
          </span>
          <input
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyTypedImage();
              }
            }}
            placeholder="acme/api:1.4.2"
            aria-label="Image reference"
            className="flex-1 min-w-0 h-10 px-3.5 rounded-control bg-navy-800 border border-card-line focus:border-action outline-none text-white font-courrier text-[0.8125rem] placeholder:text-text-faint transition-colors"
          />
          <Button
            type="primary"
            isDisabled={!manualText.trim()}
            onClick={applyTypedImage}
          >
            Use image
          </Button>
        </div>
      </>
    );
  }

  const reference = `${repository}:${tag}`;
  const sizeLabel = info ? formatSize(info.sizeBytes) : null;
  const shortDigest =
    info && info.digest ? `${info.digest.slice(0, 19)}…` : null;

  let status;

  if (isChecking) {
    status = <span className="opacity-70">Checking Docker Hub…</span>;
  } else if (info && info.exists && !info.errorCode) {
    // Only the platform we actually run is worth naming. `nginx` ships eight;
    // listing them all answered a question nobody asked.
    const runsHere = info.architectures.some(
      (architecture) =>
        architecture === REQUIRED_PLATFORM ||
        architecture.startsWith(`${REQUIRED_PLATFORM}/`)
    );

    status = (
      <span className="text-cta-green">
        Found
        {runsHere ? ` · ${REQUIRED_PLATFORM}` : ''}
        {sizeLabel ? ` · ${sizeLabel}` : ''}
        {shortDigest ? ` · ${shortDigest}` : ''}
      </span>
    );
  } else if (info) {
    status = (
      <span className="text-error-red">
        {messageForError(info.errorCode, reference)}
      </span>
    );
  } else {
    status = null;
  }

  // Next is what performs the check, so it is enabled whenever there is a
  // coherent question to ask — not once an answer is already in hand.
  const canContinue = Boolean(
    repository && tag && !credentialsError && !isChecking
  );

  return (
    <>
      {/* The chosen image keeps the row it was picked from, so choosing reads
          as that row moving up rather than as a different screen. */}
      {picked && (
        <div className={ROW_CLASSNAME}>
          <div className="flex items-center basis-2/3 max-w-[66%] min-w-0">
            <span className="flex-none w-[30px] h-[30px] rounded-surface bg-navy-700 flex items-center justify-center">
              <DockerLogo
                width="17"
                height="17"
                fill="currentColor"
                color={dockerColor}
              />
            </span>
            <span className="text-[0.9375rem] font-medium leading-140 text-light-blue ml-3.5 truncate">
              {picked.repository}
            </span>
          </div>
          <button
            type="button"
            className="text-sm leading-140 text-light-blue hover:underline hover:underline-offset-2"
            onClick={backToSearch}
          >
            Change
          </button>
        </div>
      )}

      <div className="px-[1.875rem] py-7 space-y-6">
        {/* Repository and tag are one reference — `repo:tag` — so they sit on
            one row. Stacked, the full-width repository field above a narrow
            tag field read as a broken alignment rather than a deliberate one,
            and cost a screenful of height for two short answers. */}
        <div>
          {picked ? (
            <div className="max-w-[16rem]">
              <TextInput
                label="Tag"
                labelClassName={LABEL_CLASSNAME}
                name="imageTag"
                value={tag}
                isRequired
                placeholderText="latest"
                onChange={(event) => update({ tag: event.target.value })}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_16rem] gap-4">
              <TextInput
                label="Repository"
                labelClassName={LABEL_CLASSNAME}
                name="imageRepository"
                value={repository}
                isRequired
                placeholderText="nginx or acme/api"
                onChange={(event) => update({ repository: event.target.value })}
              />
              <TextInput
                label="Tag"
                labelClassName={LABEL_CLASSNAME}
                name="imageTag"
                value={tag}
                isRequired
                placeholderText="latest"
                onChange={(event) => update({ tag: event.target.value })}
              />
            </div>
          )}

          {!picked && (
            <div className={`${HINT_CLASSNAME} mt-2`}>
              On Docker Hub. Official images like nginx need no namespace.
            </div>
          )}

          {/* Height reserved whether or not there is something to say, so
              nothing below shifts when the answer lands. */}
          <div className={`${HINT_CLASSNAME} min-h-[1.125rem] mt-2`}>
            {status}
          </div>
        </div>

        {/* Always here, never behind a switch. The old Yes/No asked the user
            to classify the image before they could type a token, and a wrong
            guess came back as an unexplained NOT_FOUND. Two fields rather
            than one `username:token` box: a combined field cannot be masked
            properly, breaks on a token containing a colon, and password
            managers cannot fill it — it is neither a username nor a password
            as far as they are concerned. */}
        <div className="space-y-3">
          <div>
            <div className={`${LABEL_CLASSNAME} leading-140`}>
              Credentials (optional)
            </div>
            {/* One line, not two. The old copy said the credentials never
                leave our build nodes above the field and that they are stored
                encrypted below it — two reassurances about the same thing,
                sandwiching one input. */}
            <div className={`${HINT_CLASSNAME} mt-1`}>
              Required for private images. Stored encrypted, never returned by
              the API, and never leaves our build nodes.
            </div>
          </div>
          <TextInput
            type={showCredentials ? 'text' : 'password'}
            name="registryCredentials"
            value={credentialsText}
            placeholderText={'Use format "username:token"'}
            isError={Boolean(credentialsError)}
            errorMessage={credentialsError}
            endAdornment={
              <button
                type="button"
                aria-label={
                  showCredentials ? 'Hide credentials' : 'Show credentials'
                }
                className="text-light-blue"
                onClick={() => setShowCredentials((shown) => !shown)}
              >
                {showCredentials ? (
                  <VisibilityOffIcon width="18" height="18" />
                ) : (
                  <VisibilityIcon width="18" height="18" />
                )}
              </button>
            }
            onChange={(event) => {
              const text = event.target.value;

              setCredentialsText(text);
              update(parseCredentials(text));
            }}
          />

        </div>

        <div className="flex justify-between items-center pt-2">
          {picked ? (
            <span />
          ) : (
            <button
              type="button"
              className="text-sm leading-140 text-light-blue hover:underline hover:underline-offset-2"
              onClick={backToSearch}
            >
              Back to search
            </button>
          )}
          <Button
            type="primary"
            isDisabled={!canContinue}
            onClick={checkAndContinue}
          >
            {isChecking ? 'Checking…' : 'Next'}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ContainerImageSource;
